import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import FaIcon from '@fortawesome/ember-fontawesome/components/fa-icon';
import Component from '@glimmer/component';
import eq from 'editor/helpers/eq';
import version from 'editor/helpers/version';
import type ProjectManagerService from 'editor/services/project-manager';
import type { DrumsetFile, SongFile } from 'editor/services/project-manager';
import StatusService from 'editor/services/status';
import fileSize from 'editor/utils/file-size';
import ErrorPopupDialog from './error-popup';
import Button from './button';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import velcro from 'ember-velcro/modifiers/velcro';
import onClickOutside from 'editor/modifiers/on-click-outside';

interface DrumsetStatusSignature {
  Element: HTMLDivElement;
  Args: {
    drumsetFile: DrumsetFile;
  };
  Blocks: {
    // default: [];
  };
}

class DrumsetFileStatus extends Component<DrumsetStatusSignature> {
  @service declare projectManager: ProjectManagerService;
  @tracked displayErrorPopup = false;
  @tracked errorData: string[] = [];
  constructor(owner: unknown, args: DrumsetStatusSignature['Args']) {
    super(owner, args);
  }

  get drumset() {
    return this.args.drumsetFile.drumset!;
  }

  drumsetMessage(drumsetFile: DrumsetFile) {
    let validity = drumsetFile.drumset!.isValid;
    if (validity == true) {
      validity = '';
    } else {
      validity = ` - ${validity}`;
    }
    return `${drumsetFile.name} - ${fileSize(drumsetFile.drumset!.size)}${validity}`;
  }

  @action areSongsValid() {
    this.errorData = [];
    this.projectManager.songFolders.forEach((folder) => {
      folder.songs.forEach((songFile) => {
        let validSong = songFile.song?.isValid;
        if (validSong === true || validSong === undefined) {
          // Do nothing, undefined means the song has not been clicked/initialized, so there is nothing wrong with it and it won't be saved
        } else {
          if (!this.errorData.includes('Errors in song')) {
            this.errorData.push(`Errors in song  ${songFile.name}:`);
          }
          this.errorData.push(`${validSong}`);
        }
      });
    });
  }



  @action areDrumsetsValid() {
    this.projectManager.drumsets.forEach((drumset) => {
      let validDrumset = drumset.drumset?.isValid;
      if (validDrumset === true || validDrumset === undefined) {
        // Do nothing, undefined means the drumset has not been clicked/initialized, so there is nothing wrong with it and it won't be saved
      } else {
        if (!this.errorData.includes('Errors in drumset')) {
          this.errorData.push(`Errors in drumset ${drumset.name}:`);
        }
        this.errorData.push(`${validDrumset}`);
      }
    });
  }

  @action toggleErrorPopup(force: boolean) {
    this.areSongsValid();
    this.areDrumsetsValid();
    this.displayErrorPopup = force;
  }

  get showError() {
    // in a most unfortunate fashion, this is the only way to get showError to update when we need
    // in the infinite wisom of embers tracking, it will not just correctly update by checking isValid
    // so we have to call this.args.drumsetFile.drumset!.isValid even though it does literally nothing
    // but, it makes sure this function is called when tracked things change.
    this.args.drumsetFile.drumset!.isValid;
    const result = (
      this.projectManager.songFolders.some((folder) =>
        folder.songs.some(
          (song) => song.song?.isValid !== true && song.song?.isValid !== undefined,
        ),
      ) ||
      this.projectManager.drumsets.some(
        (drumset) =>
          drumset.drumset?.isValid !== true && drumset.drumset?.isValid !== undefined,
      )
    );
    if (this.projectManager.verboseLoggingEnabled) {
      // Log all songs/drumsets AFTER the original .some() logic so we don't change tracking behavior
      const songStatuses = this.projectManager.songFolders.flatMap((folder) =>
        folder.songs.map((song) => {
          const songObj = (song as SongFile).song;
          return `${song.name}:${songObj ? (songObj.isValid === true ? 'valid' : 'ERROR') : 'unparsed'}`;
        }),
      );
      const drumsetStatuses = this.projectManager.drumsets.map((drumset) => {
        const dsObj = drumset.drumset;
        return `${drumset.name}:${dsObj ? (dsObj.isValid === true ? 'valid' : 'ERROR') : 'unparsed'}`;
      });
      console.log('[DrumsetFileStatus.showError] songs:', songStatuses.join(', '));
      console.log('[DrumsetFileStatus.showError] drumsets:', drumsetStatuses.join(', '));
      console.log('[DrumsetFileStatus.showError] => result:', result);
    }
    return result;
  }

  get drumsetSize() {
    return this.args.drumsetFile.drumset!.size;
  }

  <template>
    {{@drumsetFile.name}}
    -
    {{fileSize this.drumsetSize}}
    {{#if (eq false this.showError) }}
      <span class="text-green-500">✓</span>
    {{else}}
      <Button class="text-red-500 dark:text-red-500" @icon="triangle-exclamation" id="error-text-Btn-drum" {{on "click" (fn this.toggleErrorPopup true)}}>
        {{#if (eq true this.drumset.isValid) }}
          This project has some errors
        {{else}}
          {{this.drumset.isValid}}
        {{/if}}
      </Button>
    {{/if}}
    {{#if this.displayErrorPopup}}
      <div
        class="fixed bg-white border rounded shadow-md p-4 z-50 max-w-96 dark:bg-primary-darkFill dark:border-primary-darkBorderColor"
        {{velcro "#error-text-Btn-drum" placement="bottom-start"}}
        {{onClickOutside (fn this.toggleErrorPopup false)}}
      ><ErrorPopupDialog @errorData={{this.errorData}} @onClose={{fn this.toggleErrorPopup false}} /></div>
    {{/if}}
  </template>
}
interface StatusBarSignature {
  Element: HTMLDivElement;
  Args: {};
  Blocks: {};
}
export default class StatusBarComponent extends Component<StatusBarSignature> {
  @service declare status: StatusService;
  @service declare projectManager: ProjectManagerService;
  @tracked displayErrorPopup = false;
  @tracked errorData: string[] = [];
  constructor(owner: unknown, args: StatusBarSignature['Args']) {
    super(owner, args);
  }
  get drumsetFile() {
    return this.status.drumsetFile;
  }
  get songFile() {
    return this.status.songFile;
  }
  @action songFileMessage(songFile: SongFile) {
    const folderIndex = this.projectManager.songFolders.indexOf(songFile.folder);
    const channel = 0;
    const songIndex = songFile.folder.songs.indexOf(songFile);
    const folderMSB = Math.floor(folderIndex / 128);
    const folderLSB = folderIndex % 128;
    const midiInfo = `CC0:${folderMSB} CC32:${folderLSB} PC:${songIndex} {PC${folderMSB}.${folderLSB}:${songIndex}@${channel}}`;

    return `${songFile.name} - ${midiInfo}`;
  }
  @action isSongValid(songFile: SongFile) {
    let validSong = songFile.song!.isValid;
    if (validSong === true) {
      validSong = '';
    } else {
      validSong = ` - ${validSong}`;
    }

    return `${validSong}`;
  }

  @action areSongsValid() {
    this.errorData = [];
    this.projectManager.songFolders.forEach((folder) => {
      folder.songs.forEach((songFile) => {
        let validSong = songFile.song?.isValid;
        if (validSong === true || validSong === undefined) {
          // Do nothing, undefined means the song has not been clicked/initialized, so there is nothing wrong with it and it won't be saved
        } else {
          if (!this.errorData.includes('Errors in song')) {
            this.errorData.push(`Errors in song ${songFile.name}:`);
          }
          this.errorData.push(`${validSong}`);
        }
      });
    });
  }

  @action areDrumsetsValid() {
    this.projectManager.drumsets.forEach((drumset) => {
      let validDrumset = drumset.drumset?.isValid;
      if (validDrumset === true || validDrumset === undefined) {
        // Do nothing, undefined means the drumset has not been clicked/initialized, so there is nothing wrong with it and it won't be saved
      } else {
        if (!this.errorData.includes('Errors in drumset')) {
          this.errorData.push(`Errors in drumset ${drumset.name}:`);
        }
        this.errorData.push(`${validDrumset}`);
      }
    });
  }

  get showError() {
    const result = (
      this.projectManager.songFolders.some((folder) =>
        folder.songs.some(
          (song) => song.song?.isValid !== true && song.song?.isValid !== undefined,
        ),
      ) ||
      this.projectManager.drumsets.some(
        (drumset) =>
          drumset.drumset?.isValid !== true && drumset.drumset?.isValid !== undefined,
      )
    );
    if (this.projectManager.verboseLoggingEnabled) {
      // Log all songs/drumsets AFTER the original .some() logic so we don't change tracking behavior
      const songStatuses = this.projectManager.songFolders.flatMap((folder) =>
        folder.songs.map((song) => {
          const songObj = (song as SongFile).song;
          return `${song.name}:${songObj ? (songObj.isValid === true ? 'valid' : 'ERROR') : 'unparsed'}`;
        }),
      );
      const drumsetStatuses = this.projectManager.drumsets.map((drumset) => {
        const dsObj = drumset.drumset;
        return `${drumset.name}:${dsObj ? (dsObj.isValid === true ? 'valid' : 'ERROR') : 'unparsed'}`;
      });
      console.log('[StatusBar.showError] songFile:', this.status.songFile?.name);
      console.log('[StatusBar.showError] songs:', songStatuses.join(', '));
      console.log('[StatusBar.showError] drumsets:', drumsetStatuses.join(', '));
      console.log('[StatusBar.showError] => result:', result);
    }
    return result;
  }

  @action toggleErrorPopup(force: boolean) {
    this.areSongsValid();
    this.areDrumsetsValid();
    this.displayErrorPopup = force;
  }

  drumsetMessage(drumsetFile: DrumsetFile) {
    let validity = drumsetFile.drumset!.isValid;
    if (validity == true) {
      validity = '';
    } else {
      validity = ` - ${validity}`;
    }
    return `${drumsetFile.name} - ${fileSize(drumsetFile.drumset!.size)}${validity}`;
  }

  <template>
    <div class="grid grid-cols-[1fr_max-content_1fr] gap-4">
      <div data-test-left class="flex flex-row items-center gap-2">
        {{#if this.drumsetFile}}
          <DrumsetFileStatus @drumsetFile={{this.drumsetFile}} />
        {{else if this.songFile}}
          {{this.songFileMessage this.songFile}}
          {{#if (eq false this.showError)}}
            <span class="text-green-500">✓</span>
          {{else}}
            <Button class="text-red-500 dark:text-red-500 overflow-hidden w-auto text-ellipsis min-w-[0px] max-w-[500px] truncate" @icon="triangle-exclamation" id="error-text-Btn-song" {{on "click" (fn this.toggleErrorPopup true)}}>
              {{#if (eq true this.songFile.song.isValid) }}
                This project has some errors
              {{else}}
               {{this.isSongValid this.songFile}}
              {{/if}}
            </Button>
          {{/if}}
        {{else}}
          Welcome to BeatBuddy Manager!
        {{/if}}
      </div>
      <div data-test-version class="flex text-center items-center border-l border-r border-gray-300 px-4 dark:border-primary-darkBorderColor">
        <a
          href="https://www.singularsound.com/pages/bbmo-changelog"
          target="_blank"
          rel="noopener noreferrer"
        >{{version}}</a>
      </div>
      <div data-test-right>{{! right }}</div>
    </div>
    {{#if this.displayErrorPopup}}
      <div
        class="fixed bg-white border rounded shadow-md p-4 z-50 max-w-96 dark:bg-primary-darkFill dark:border-primary-darkBorderColor"
        {{velcro "#error-text-Btn-song" placement="bottom-start"}}
        {{onClickOutside (fn this.toggleErrorPopup false)}}
      ><ErrorPopupDialog @errorData={{this.errorData}} @onClose={{fn this.toggleErrorPopup false}} /></div>
    {{/if}}
  </template>
}
declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    StatusBar: typeof StatusBarComponent;
  }
}
